<template>
    <div v-if="show">
        <div class="overlay" @click="dismiss"></div>
        <div class="problemLayer">
            <h2 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
                {{ $t('Es gibt einen neuen Hinweis. | Es gibt neue Hinweise.', { count: problems.length }) }}
            </h2>
            <table class="kgr-table kgr-table--striped kgr-table--block table-groups">
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Hinweise') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        <th class="kgr-table__cell kgr-table__cell--headline"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="problem in problems"
                        :key="problem.id"
                        class="kgr-table__row kgr-table__row--top"
                    >
                        <td class="kgr-table__cell">
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <div v-html="problem.hint"></div>
                        </td>
                        <td class="kgr-table__cell">
                            <i
                                v-if="problem.isResolved"
                                :class="resolutionIsInfo(problem) ? 'icon-info' : 'icon-check'"
                                :style="{ color: resolutionIsInfo(problem) ? '#2196F3' : '#4CAF50' }"
                            ></i>
                            <template v-else>
                                <kgr-loading
                                    v-show="problem.busy"
                                    inline
                                />
                                <SphButton
                                    v-show="!problem.busy"
                                    variant="ghost"
                                    :icon="RefreshCw"
                                    :title="$t('Aktualisieren')"
                                    :aria-label="$t('Aktualisieren')"
                                    @click="update(problem)"
                                />
                            </template>
                        </td>
                        <td class="kgr-table__cell">
                            <span v-if="problem.isResolved">{{ resolutionLabel(problem) }}</span>
                            <kgr-select
                                v-else
                                v-model="problem.solution"
                                :options="problem.solutions"
                                :disabled="problem.busy"
                                no-null-option
                                display="label"
                            />
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-button
                                v-if="!problem.isResolved"
                                type="red"
                                :disabled="problem.busy || !problem.solution"
                                @click="solve(problem)"
                            >
                                {{ $t('ausführen') }}
                            </kgr-button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="kgr-postfix kgr-postfix--center">
                <div class="kgr-postfix__fluid kgr-text kgr-text--small">
                    {{ $t('Eine Übersicht aller Hinweise finden Sie jederzeit im Bereich Hinweise.') }}
                </div>
                <div class="kgr-postfix__fixed">
                    <kgr-button
                        type="blue"
                        :value="$t('Schließen')"
                        @click="dismiss"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '../../js/service/EventBus';
import GenericSelect from '../../components/common/form/select/GenericSelect.vue';
import KgrButton from '../../components/common/form/button/KgrButton.vue';
import Loading from '../../components/common/Loading.vue';
import { RefreshCw } from 'lucide';
import { SphButton } from '@/components/base/button/index.ts';
import hintRepositoryFor from '../../js/repository/hintRepositoryFor';

export default {
    name: 'HintOverlay',
    components: {
        'kgr-button': KgrButton,
        'kgr-loading': Loading,
        'kgr-select': GenericSelect,
        SphButton,
    },
    props: {
        institutionType: {
            type: String,
            default: 'club',
            validator: (value) => ['club', 'kindergarten'].includes(value),
        },
        institutionId: {
            type: Number,
            required: true,
        },
        url: {
            type: String,
            required: false,
            default: '',
        },
    },
    data() {
        return {
            RefreshCw,
            problems: [],
            dismissed: false,
            lastUuids: undefined,
        };
    },
    computed: {
        repository() {
            return hintRepositoryFor(this.institutionType);
        },
        show() {
            // Deliberately ignores `loading`: a background reload (own solve triggering a new problem,
            // the "Aktualisieren" recheck) must merge into the still-visible list, not blank the overlay
            // out first - `problems.length` alone already keeps it hidden until the very first load lands.
            return !this.dismissed && this.problems.length > 0;
        },
    },
    created() {
        // The overlay must only open when a hint is actually generated, not on every page load.
        // A generated hint sets a server-side flash that FractalSubscriber turns into the
        // X-Problems-Available response header, which the axios interceptor relays as this event -
        // together with the precise uuids of the problems that request created (X-New-Problem-Uuids),
        // so loadProblems() can fetch exactly those instead of everything new in the institution.
        this.unbindProblemCreated = EventBus.$on('hint:problem-created', (uuids) => {
            this.dismissed = false;
            this.loadProblems(uuids);
        });
    },
    beforeUnmount() {
        this.unbindProblemCreated?.();
    },
    methods: {
        dismiss() {
            this.dismissed = true;
        },
        loadProblems(uuids) {
            // Remember the uuids this load was scoped to, so a later no-args reload (e.g. after
            // update() re-evaluates a single problem) stays scoped to the same batch instead of
            // falling back to the institution-wide query.
            if (uuids !== undefined) {
                this.lastUuids = uuids;
            }
            this.repository.getNew(this.institutionId, this.lastUuids)
                .then((problems) => {
                    this.mergeProblems(problems);
                })
                .catch(() => {
                    // A failed refresh should not wipe problems already shown (solved or not).
                });
        },
        // Solving one problem can synchronously create another (e.g. measles dose 1 -> dose 2 missing),
        // which fires this same reload while the overlay is still open. A wholesale replace would both
        // drop the row the admin just solved (losing its checkmark/label) and hard-swap the whole table
        // out from under them the instant the new problem appears. So already-resolved rows are kept as-is
        // regardless of the fresh server list, still-unresolved rows are refreshed from the server response
        // (or dropped if the server no longer reports them, e.g. resolved elsewhere), and anything genuinely
        // new gets appended.
        mergeProblems(fetchedProblems) {
            const fetchedById = new Map(fetchedProblems.map((problem) => [problem.id, problem]));

            const merged = this.problems
                .filter((problem) => problem.isResolved || fetchedById.has(problem.id))
                .map((problem) => {
                    if (problem.isResolved) {
                        return problem;
                    }

                    const fresh = fetchedById.get(problem.id);
                    fetchedById.delete(problem.id);

                    return {
                        ...fresh,
                        solution: problem.solution ?? (fresh.solutions && fresh.solutions.length > 0 ? fresh.solutions[0] : null),
                        busy: false,
                        isResolved: false,
                        resolutionState: null,
                    };
                });

            const newlyFetched = Array.from(fetchedById.values()).map((problem) => ({
                ...problem,
                solution: problem.solutions && problem.solutions.length > 0 ? problem.solutions[0] : null,
                busy: false,
                isResolved: false,
                resolutionState: null,
            }));

            this.problems = [...merged, ...newlyFetched];
        },
        solve(problem) {
            if (problem.busy || !problem.solution) {
                return;
            }
            problem.busy = true;
            this.repository.solveProblem(problem.id, problem.solution.value)
                .then((result) => {
                    problem.resolutionState = this.resolutionStateFromResult(result);
                    problem.isResolved = true;
                })
                .finally(() => {
                    problem.busy = false;
                });
        },
        resolutionStateFromResult(result) {
            // result.isPresent reflects the problem's own isPresent() re-checked AFTER the solution already
            // ran (see ProblemSolver::solve()), so `false` here means this solve is what fixed it - that's a
            // plain "done", not "no longer relevant". hasFollowUp/isIgnored still take precedence since those
            // reflect the solution the admin actually picked (Ignore/FollowUp), independent of isPresent.
            if (result.hasFollowUp) {
                return 'follow_up';
            }
            if (result.isIgnored) {
                return 'ignored';
            }
            return 'done';
        },
        resolutionLabel(problem) {
            switch (problem.resolutionState) {
                case 'follow_up':
                    return this.$t('Wiedervorlage');
                case 'ignored':
                    return this.$t('Ignoriert');
                default:
                    return this.$t('Erledigt');
            }
        },
        resolutionIsInfo(problem) {
            return problem.resolutionState === 'follow_up' || problem.resolutionState === 'ignored';
        },
        update(problem) {
            if (problem.busy) {
                return;
            }
            problem.busy = true;
            this.repository.updateProblem(problem.id)
                .then(() => this.loadProblems())
                .finally(() => {
                    // loadProblems() swallows its own fetch errors (so an in-flight reload never blanks
                    // already-shown problems), which means it never rejects to signal failure here. A
                    // successful reload already resets this via a fresh object in mergeProblems(), so this
                    // is a no-op there; on a failed updateProblem() or getNew(), it's what stops the row
                    // being stuck on its busy spinner forever.
                    problem.busy = false;
                });
        },
    },
};
</script>

<style scoped lang="scss">
    .overlay {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.7);
    z-index: 1000;
}
.problemLayer {
    position: absolute;
    top: 30%;
    width: 80%;
    left: 10%;
    border: 4px solid #888888;
    height: auto;
    background: white;
    border-radius: 5px;
    z-index: 1001;
    padding: 10px;
}
</style>
